import { useApolloClient } from "@apollo/client";
import { getDataOrNull, omit } from "@msys/common";
import {
  CardItem,
  DataGrid,
  DebouncedSearchInput,
  ErrorMessage,
  FormattedPrice,
  GridColDef,
  ListHeader,
  ListHeaderActiveFilters,
  ListHeaderActiveFiltersActionButton,
  LoadingSpinner,
  ModalOpenButton,
  useFormatting,
  useScreenWidth,
} from "@msys/ui";
import { Close as CloseIcon, Done as DoneIcon } from "@mui/icons-material";
import {
  Divider,
  Link as MuiLink,
  Stack,
  TablePagination,
  Typography,
} from "@mui/material";
import { getGridNumericOperators } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  IncomingQuotesSortBy,
  IncomingQuotesSorting,
  OutgoingQuotesSortBy,
  OutgoingQuotesSorting,
  SortDirection,
} from "../../../clients/graphqlTypes.js";
import { useRestrictionFilter } from "../../auth/useRestrictionFilter.js";
import { EntityNumber } from "../../commons/EntityNumber.js";
import { TabsWithRestriction } from "../../commons/TabsWithRestriction.js";
import { SwitchCollectionViewButton } from "../../commons/button/SwitchCollectionViewButton.js";
import { FilterButton } from "../../commons/filters/FilterButton.js";
import { useFiltersAndPagination } from "../../commons/filters/useFiltersAndPagination.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../commons/hooks/useCollectionView.js";
import { useStateWithUrlParam } from "../../commons/hooks/useStateWithUrlParam.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import {
  PAGE_LIST_RESULTS_PER_PAGE,
  RESULTS_PER_PAGE_OPTIONS,
} from "../../constants.js";
import { useGetCustomFieldValueAsDisplayContent } from "../../features/custom-fields/CustomFieldsBox.js";
import { useCustomFieldConfig } from "../../features/custom-fields/useCustomFieldConfig.js";
import { QuotesFilterModal } from "../../features/documents/QuotesFilterModal.js";
import { QuotesAcceptanceFilterButton } from "../../features/documents/filters/QuotesAcceptance.js";
import { QuotesBindingFilterButton } from "../../features/documents/filters/QuotesBinding.js";
import { QuotesPublishingFilterButton } from "../../features/documents/filters/QuotesPublishing.js";
import {
  BINDING_TYPES_MAPPING,
  QuoteBindingTypeFilter,
} from "../../features/documents/useBindingTypes.js";
import {
  PUBLISHING_STATUS_MAPPING,
  QuotePublishingStatusFilter,
} from "../../features/documents/usePublishingStatus.js";
import { useQuotesActiveFilterChips } from "../../features/documents/useQuotesActiveFilterChips.js";
import { QuoteListItem } from "../../features/quotes/QuoteListItem.js";
import {
  IncomingQuoteStatusBadge,
  QuoteStatusBadge,
} from "../../features/quotes/QuoteStatusBadge.js";
import { RequestStatusBadge } from "../../features/requests/RequestStatusBadge.js";
import { RequestOverviewProjectOutgoingQuoteModal } from "../../features/requests/modals/RequestOverviewModal.js";
import { TeamsFilterButton } from "../../features/teams/filters/TeamsFilter.js";
import { useTeamFilterDefaultSeeding } from "../../features/teams/filters/useTeamFilterDefaultSeeding.js";
import { useUserData } from "../../auth/useUserData.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import { useListViewCustomQuickFilters } from "../../features/users/useListViewCustomQuickFilters.js";
import {
  DocumentsIncomingQuotesQueryVariables,
  DocumentsOutgoingQuotesQueryVariables,
  IncomingQuoteFragment,
  OutgoingQuoteFragment,
  useDocumentsIncomingQuotesQuery,
  useDocumentsOutgoingQuotesQuery,
} from "./Quotes.generated.js";

const ALLOWED_VIEWS: CollectionView[] = ["table", "list"];

type Tab = "INCOMING" | "OUTGOING";

interface Props {
  submenuItems: PageTopbarItem[];
}

export function Quotes({ submenuItems }: Props) {
  const { t } = useTranslate(["Quotes", "SidebarItems", "PageNotFound"]);

  const initialOptions = [
    {
      value: "OUTGOING" as Tab,
      label: t("Outgoing", { ns: "Quotes" }),
      capability: "QUOTING" as const,
    },
    {
      value: "INCOMING" as Tab,
      label: t("Incoming", { ns: "Quotes" }),
    },
  ];
  const restrictionFilter = useRestrictionFilter();
  const options = initialOptions.filter(restrictionFilter);

  const [tab, setTab] = useStateWithUrlParam<Tab>(
    "tab",
    options[0].value,
    true
  );

  const SectionTabs =
    initialOptions.length > 1 ? (
      <TabsWithRestriction
        options={initialOptions}
        value={tab}
        onChange={newQuickFilter => {
          setTab(newQuickFilter);
        }}
        condensed
        useSelectOnMobile
      />
    ) : undefined;

  return (
    <Page
      subtitle={t("Documents", {
        ns: "SidebarItems",
      })}
      title={t("Quotes", {
        ns: "Quotes",
      })}
      submenuItems={submenuItems}
    >
      <PageContainer>
        {tab === "OUTGOING" &&
          (options.some(o => o.value === "OUTGOING") ? (
            <OutgoingQuotesTable SectionTabs={SectionTabs} />
          ) : (
            <ErrorMessage
              message={t("Page Not Found", {
                ns: "PageNotFound",
              })}
            />
          ))}
        {tab === "INCOMING" && (
          <IncomingQuotesTable SectionTabs={SectionTabs} />
        )}
      </PageContainer>
    </Page>
  );
}

type IncomingQuotesFilters = Omit<
  DocumentsIncomingQuotesQueryVariables,
  "limit" | "offset" | "sorting" | "filterIsBinding"
> & {
  filterIsBinding?: QuoteBindingTypeFilter[];
};

const INCOMING_QUOTES_DEFAULT_SORTING: IncomingQuotesSorting[] = [
  {
    column: "createdAt",
    direction: "desc",
  },
];

function IncomingQuotesTable({
  SectionTabs,
}: {
  SectionTabs?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const { t } = useTranslate(["Global", "Quotes"]);
  const { getFormattedDate } = useFormatting();
  const { isMinTablet } = useScreenWidth();

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>("quotes", "table", "list");

  const {
    offset,
    limit,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    toRemoveParams,
  } = useFiltersAndPagination<IncomingQuotesSorting, IncomingQuotesFilters>(
    {},
    INCOMING_QUOTES_DEFAULT_SORTING,
    PAGE_LIST_RESULTS_PER_PAGE
  );

  const { customQuickFilterSelect, addCustomFilterButton } =
    useListViewCustomQuickFilters<IncomingQuotesFilters>(
      "IncomingQuotes",
      filters,
      setFilters
    );

  // don't send publishing/binding filters if both are selected as they are boolean
  const serializedFilters = React.useMemo<
    Partial<DocumentsIncomingQuotesQueryVariables>
  >(() => {
    const { filterIsBinding, ...restFilters } = filters;
    const serialized: Partial<DocumentsIncomingQuotesQueryVariables> = {
      ...restFilters,
    };

    if (filterIsBinding && filterIsBinding.length === 1) {
      serialized.filterIsBinding = BINDING_TYPES_MAPPING[filterIsBinding[0]];
    }

    return serialized;
  }, [filters]);

  const QUERY_BASE_VARIABLES = {
    limit,
    offset,
    sorting: INCOMING_QUOTES_DEFAULT_SORTING,
  };

  const client = useApolloClient();
  const query = useDocumentsIncomingQuotesQuery({
    client,
    variables: {
      ...QUERY_BASE_VARIABLES,
      sorting,
      ...serializedFilters,
    },
  });
  const quotes =
    getDataOrNull(
      query.data?.incomingQuotes ?? query.previousData?.incomingQuotes
    )?.edges?.map(e => e.node) ?? [];
  const totalCount =
    getDataOrNull(
      query.data?.incomingQuotes ?? query.previousData?.incomingQuotes
    )?.totalCount ?? 0;

  const columns = React.useMemo(
    (): GridColDef<IncomingQuoteFragment>[] => [
      {
        field: "number",
        headerName: t("No.", {
          ns: "Global",
        }),
        editable: false,
        minWidth: 80,
        flex: 1,
        renderCell: ({ row: quote }) => (
          <EntityNumber noLabel number={quote.number} />
        ),
      },
      {
        field: "docActorContractorName",
        headerName: t("Contractor", {
          ns: "Global",
        }),
        editable: false,
        sortable: false,
        flex: 2,
        minWidth: 100,
        valueGetter: (value, quote) => quote.docActorContractorName,
      },
      {
        field: "title",
        headerName: t("Title", {
          ns: "Quotes",
        }),
        editable: false,
        flex: 3,
        minWidth: 120,
        renderCell: ({ row: quote }) => quote.title,
      },
      {
        field: "projectNumber",
        headerName: t("Project", {
          ns: "Quotes",
        }),
        editable: false,
        flex: 1,
        minWidth: 80,
        valueGetter: (value, quote) => quote?.projectNumber ?? "",
        renderCell: ({ row: quote }) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <MuiLink
              component={Link}
              to={`/projects/${quote.projectId}`}
              onClick={e => {
                e.stopPropagation();
              }}
            >
              #{quote.projectNumber}
            </MuiLink>
          </Stack>
        ),
      },
      {
        field: "outgoingRequest",
        headerName: t("Related request", {
          ns: "Quotes",
        }),
        editable: false,
        flex: 3,
        minWidth: 120,
        valueGetter: (value, quote) => quote.outgoingRequest?.title ?? "",
        renderCell: ({ row: quote }) =>
          quote.outgoingRequest ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <ModalOpenButton
                Modal={RequestOverviewProjectOutgoingQuoteModal}
                modalProps={{
                  requestId: quote.outgoingRequest.id,
                  projectId: quote.projectId,
                }}
              >
                <MuiLink
                  onClick={e => {
                    e.stopPropagation();
                  }}
                >
                  {quote.outgoingRequest.title}
                </MuiLink>
              </ModalOpenButton>
              <RequestStatusBadge request={quote.outgoingRequest} small />
            </Stack>
          ) : (
            "–"
          ),
      },
      {
        field: "isBinding",
        headerName: t("Binding", {
          ns: "Quotes",
        }),
        editable: false,
        sortable: false,
        type: "boolean",
        flex: 1,
        renderCell: ({ row: quote }) =>
          quote.isBinding ? (
            <DoneIcon fontSize="small" />
          ) : (
            <CloseIcon fontSize="small" color="secondary" />
          ),
      },
      {
        field: "proposedTotalPrice",
        headerName: t("Proposed Price", {
          ns: "Quotes",
        }),
        editable: false,
        sortable: true,
        type: "number",
        flex: 1,
        align: "right",
        headerAlign: "right",
        minWidth: 80,
        valueGetter: (value, quote) => quote.proposedTotalPrice || 0,
        renderCell: ({ row: quote }) => {
          return (
            <Typography
              component="span"
              align="right"
              variant="body2"
              style={{ fontWeight: 500 }}
            >
              <FormattedPrice value={quote.proposedTotalPrice || 0} />
            </Typography>
          );
        },
        filterOperators: getGridNumericOperators().filter(operator =>
          [">", "<", ">=", "<="].includes(operator.value)
        ),
      },
      {
        field: "agreedTotalPrice",
        headerName: t("Agreed Price", {
          ns: "Quotes",
        }),
        editable: false,
        sortable: false,
        type: "number",
        flex: 1,
        align: "right",
        headerAlign: "right",
        minWidth: 80,
        valueGetter: (value, quote) => quote.agreedTotalPrice || 0,
        renderCell: ({ row: quote }) => {
          return quote.agreedTotalPrice === null ? (
            "–"
          ) : (
            <Typography
              component="span"
              align="right"
              variant="body2"
              style={{ fontWeight: 500 }}
            >
              <FormattedPrice value={quote.agreedTotalPrice || 0} />
            </Typography>
          );
        },
        filterOperators: getGridNumericOperators().filter(operator =>
          [">", "<", ">=", "<="].includes(operator.value)
        ),
      },
      {
        field: "status",
        headerName: t("Status", {
          ns: "Quotes",
        }),
        editable: false,
        sortable: false,
        flex: 1,
        minWidth: 80,
        renderCell: ({ row: quote }) => (
          <IncomingQuoteStatusBadge small quote={quote} />
        ),
      },
      {
        field: "publishedAt",
        headerName: t("Received", {
          ns: "Quotes",
        }),
        editable: false,
        sortable: true,
        type: "date",
        flex: 1,
        minWidth: 80,
        valueGetter: (value, quote) =>
          quote.publishedAt ? new Date(quote.publishedAt) : null,
        renderCell: ({ row: quote }) =>
          quote.publishedAt ? getFormattedDate(quote.publishedAt) : "–",
      },
      {
        field: "acceptedAt",
        headerName: t("Accepted", {
          ns: "Quotes",
        }),
        editable: false,
        sortable: true,
        type: "date",
        flex: 1,
        minWidth: 80,
        valueGetter: (value, quote) =>
          quote.acceptedAt ? new Date(quote.acceptedAt) : null,
        renderCell: ({ row: quote }) =>
          quote.acceptedAt ? getFormattedDate(quote.acceptedAt) : "–",
      },
    ],
    [getFormattedDate, t]
  );

  const getToPath = (item: { projectId: string; id: string }) =>
    `/projects/${item.projectId}/quotes/${item.id}`;

  const stateStore = useDataGridStateStore("IncomingQuotes");
  const activeFilterChips = useQuotesActiveFilterChips({
    filters,
    setFilters,
  });

  return (
    <>
      <ListHeader
        SwitchViewButton={
          <SwitchCollectionViewButton
            allowedViews={ALLOWED_VIEWS}
            activeView={activeView}
            setActiveView={setActiveView}
          />
        }
        QuickFilter={
          <Stack direction="row" spacing={1} alignItems="center">
            {SectionTabs && (
              <>
                <div>{SectionTabs}</div>
                <Divider orientation="vertical" flexItem />
              </>
            )}
            <div>
              {isMinTablet ? (
                <>
                  <QuotesBindingFilterButton
                    value={filters.filterIsBinding ?? []}
                    setValue={newValue =>
                      setFilters(state =>
                        newValue.length > 0
                          ? {
                              ...state,
                              filterIsBinding: newValue,
                            }
                          : omit(state, "filterIsBinding")
                      )
                    }
                  />
                  <QuotesAcceptanceFilterButton
                    value={filters.filterAgreement ?? []}
                    setValue={newValue =>
                      setFilters(state =>
                        newValue.length > 0
                          ? {
                              ...state,
                              filterAgreement: newValue,
                            }
                          : omit(state, "filterAgreement")
                      )
                    }
                  />
                </>
              ) : undefined}
              {customQuickFilterSelect}
            </div>
          </Stack>
        }
        FilterButton={
          !isMinTablet ? (
            <ModalOpenButton
              Modal={QuotesFilterModal}
              modalProps={{
                filters,
                setFilters,
                resetFilters,
                tab: "incoming",
              }}
            >
              <FilterButton />
            </ModalOpenButton>
          ) : undefined
        }
        SearchField={
          <DebouncedSearchInput
            placeholder={t("Search", {
              ns: "Global",
            })}
            defaultValue={filters.searchTerm}
            onChangeSearchTerm={(newValue: string) => {
              setFilters(filters => ({
                ...filters,
                searchTerm: newValue,
              }));
            }}
          />
        }
        ActiveFilterChips={
          <ListHeaderActiveFilters
            activeFilterChips={activeFilterChips}
            clearAllButton={
              <ListHeaderActiveFiltersActionButton
                label={t("Clear all", { ns: "Global" })}
                onClick={resetFilters}
              />
            }
            collapsedContent={
              <Typography variant="body2">
                {t("Active filters", { ns: "Global" })}
                <Typography
                  variant="body2"
                  component="span"
                  marginLeft={1}
                  fontWeight="bold"
                >
                  {t("{count} results", {
                    ns: "Global",
                    count: activeFilterChips.length,
                  })}
                </Typography>
              </Typography>
            }
            saveFiltersButton={addCustomFilterButton}
          />
        }
        marginBottom={2}
      />
      {(() => {
        switch (activeView) {
          case "table":
            return (
              <DataGrid
                stateStore={stateStore}
                loading={query.loading}
                hideFooter={totalCount === 0}
                columns={columns}
                rows={quotes}
                onRowClick={({ row }) => {
                  navigate(getToPath(row as IncomingQuoteFragment));
                }}
                sortModel={sorting.map(s => ({
                  field: s.column,
                  sort: s.direction,
                }))}
                onSortModelChange={newModel => {
                  setSorting(
                    newModel.map(({ field, sort }) => ({
                      column: field as IncomingQuotesSortBy,
                      direction: (sort ?? "asc") as SortDirection,
                    }))
                  );
                }}
                paginationModel={paginationModel}
                onPaginationModelChange={newPaginationModel => {
                  setPaginationModel(newPaginationModel);
                }}
                pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
                rowCount={totalCount}
              />
            );
          case "list":
            if (query.loading) return <LoadingSpinner />;
            if (totalCount === 0)
              return (
                <ErrorMessage
                  message={t("There are no items to display", {
                    ns: "Global",
                  })}
                />
              );
            return (
              <Stack width="100%" direction="column" spacing={1}>
                {quotes.map(quote => (
                  <CardItem
                    key={quote.id}
                    //@ts-ignore
                    component={Link}
                    to={getToPath(quote)}
                  >
                    <QuoteListItem quote={quote} />
                  </CardItem>
                ))}
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={paginationModel.page}
                  onPageChange={(event, newPage) => {
                    setPaginationModel({
                      pageSize: paginationModel.pageSize,
                      page: newPage,
                    });
                  }}
                  rowsPerPage={paginationModel.pageSize}
                  onRowsPerPageChange={event => {
                    setPaginationModel({
                      pageSize: parseInt(event.target.value, 10),
                      page: 0,
                    });
                  }}
                  labelRowsPerPage={t("Per page:", { ns: "Global" })}
                />
              </Stack>
            );
        }
      })()}
    </>
  );
}

export type OutgoingQuotesFilters = Omit<
  DocumentsOutgoingQuotesQueryVariables,
  | "limit"
  | "offset"
  | "sorting"
  | "quotingCapability"
  | "filterIsPublished"
  | "filterIsBinding"
> & {
  filterIsPublished?: QuotePublishingStatusFilter[];
  filterIsBinding?: QuoteBindingTypeFilter[];
};

const OUTGOING_QUOTES_DEFAULT_SORTING: OutgoingQuotesSorting[] = [
  {
    column: "createdAt",
    direction: "desc",
  },
];

function OutgoingQuotesTable({
  SectionTabs,
}: {
  SectionTabs?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const { t } = useTranslate(["Global", "Quotes"]);
  const { getFormattedDate } = useFormatting();
  const { isMinTablet } = useScreenWidth();

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>("quotes", "table", "list");

  const { getFieldDisplayValueForList } =
    useGetCustomFieldValueAsDisplayContent();
  const { customFieldConfig: customFieldConfigs } =
    useCustomFieldConfig("Quote");

  const {
    offset,
    limit,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    toRemoveParams,
  } = useFiltersAndPagination<OutgoingQuotesSorting, OutgoingQuotesFilters>(
    {},
    OUTGOING_QUOTES_DEFAULT_SORTING,
    PAGE_LIST_RESULTS_PER_PAGE
  );

  const viewer = useUserData().currentUser!;
  useTeamFilterDefaultSeeding<OutgoingQuotesFilters, "filterTeamIds">({
    filters,
    setFilters,
    viewerActiveTeamIds: viewer.teams.map(t => t.id),
    filterKey: "filterTeamIds",
  });

  const { customQuickFilterSelect, addCustomFilterButton } =
    useListViewCustomQuickFilters<OutgoingQuotesFilters>(
      "OutgoingQuotes",
      filters,
      setFilters
    );

  // don't send publishing/binding filters if both are selected as they are boolean
  const serializedFilters = React.useMemo<
    Partial<DocumentsOutgoingQuotesQueryVariables>
  >(() => {
    const { filterIsPublished, filterIsBinding, ...restFilters } = filters;
    const serialized: Partial<DocumentsOutgoingQuotesQueryVariables> = {
      ...restFilters,
    };

    if (filterIsPublished && filterIsPublished.length === 1) {
      serialized.filterIsPublished =
        PUBLISHING_STATUS_MAPPING[filterIsPublished[0]];
    }

    if (filterIsBinding && filterIsBinding.length === 1) {
      serialized.filterIsBinding = BINDING_TYPES_MAPPING[filterIsBinding[0]];
    }

    return serialized;
  }, [filters]);

  const QUERY_BASE_VARIABLES = {
    limit,
    offset,
    sorting: OUTGOING_QUOTES_DEFAULT_SORTING,
  };

  const client = useApolloClient();
  const query = useDocumentsOutgoingQuotesQuery({
    client,
    variables: {
      ...QUERY_BASE_VARIABLES,
      sorting,
      ...serializedFilters,
    },
  });
  const quotes = React.useMemo(
    () =>
      getDataOrNull(
        (query.data ?? query.previousData)?.outgoingQuotes
      )?.edges?.map(e => e.node) ?? [],
    [query.data, query.previousData]
  );
  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.outgoingQuotes)
      ?.totalCount ?? 0;

  const columns = React.useMemo(
    (): GridColDef<OutgoingQuoteFragment>[] => [
      {
        field: "number",
        headerName: t("No.", {
          ns: "Global",
        }),
        editable: false,
        minWidth: 80,
        flex: 1,
        renderCell: ({ row: quote }) => (
          <EntityNumber noLabel number={quote.number} />
        ),
      },
      {
        field: "createdBy",
        headerName: t("Created by", {
          ns: "Quotes",
        }),
        editable: false,
        flex: 2,
        minWidth: 100,
        valueGetter: (value, quote) => quote.createdBy,
      },
      {
        field: "title",
        headerName: t("Title", {
          ns: "Quotes",
        }),
        editable: false,
        flex: 3,
        minWidth: 120,
        renderCell: ({ row: quote }) => quote.title,
      },
      {
        field: "projectNumber",
        headerName: t("Project", {
          ns: "Quotes",
        }),
        editable: false,
        flex: 1,
        minWidth: 80,
        valueGetter: (value, quote) => quote.projectNumber,
        renderCell: ({ row: quote }) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <MuiLink
              component={Link}
              to={`/projects/${quote.projectId}`}
              onClick={e => {
                e.stopPropagation();
              }}
            >
              #{quote.projectNumber}
            </MuiLink>
          </Stack>
        ),
      },
      {
        field: "docActorContracteeName",
        headerName: t("Contractee", {
          ns: "Global",
        }),
        editable: false,
        sortable: false,
        flex: 2,
        minWidth: 100,
        valueGetter: (value, quote) => quote.docActorContracteeName,
      },
      {
        field: "docActorClientName",
        headerName: t("End client", {
          ns: "Global",
        }),
        editable: false,
        sortable: false,
        flex: 2,
        minWidth: 100,
        valueGetter: (value, quote) => quote.docActorClientName,
      },
      {
        field: "isBinding",
        headerName: t("Binding", {
          ns: "Quotes",
        }),
        editable: false,
        sortable: false,
        type: "boolean",
        flex: 1,
        renderCell: ({ row: quote }) =>
          quote.isBinding ? (
            <DoneIcon fontSize="small" />
          ) : (
            <CloseIcon fontSize="small" color="secondary" />
          ),
      },
      {
        field: "proposedTotalPrice",
        headerName: t("Proposed Price", {
          ns: "Quotes",
        }),
        editable: false,
        sortable: true,
        type: "number",
        flex: 1,
        align: "right",
        headerAlign: "right",
        minWidth: 80,
        valueGetter: (value, quote) => quote.proposedTotalPrice || 0,
        renderCell: ({ row: quote }) => {
          return (
            <Typography
              component="span"
              align="right"
              variant="body2"
              style={{ fontWeight: 500 }}
            >
              <FormattedPrice value={quote.proposedTotalPrice ?? 0} />
            </Typography>
          );
        },
        filterOperators: getGridNumericOperators().filter(operator =>
          [">", "<", ">=", "<="].includes(operator.value)
        ),
      },
      {
        field: "agreedTotalPrice",
        headerName: t("Agreed Price", {
          ns: "Quotes",
        }),
        editable: false,
        sortable: false,
        type: "number",
        flex: 1,
        align: "right",
        headerAlign: "right",
        minWidth: 80,
        valueGetter: (value, quote) => quote.agreedTotalPrice || 0,
        renderCell: ({ row: quote }) => {
          return quote.agreedTotalPrice === null ? (
            "–"
          ) : (
            <Typography
              component="span"
              align="right"
              variant="body2"
              style={{ fontWeight: 500 }}
            >
              <FormattedPrice value={quote.agreedTotalPrice || 0} />
            </Typography>
          );
        },
        filterOperators: getGridNumericOperators().filter(operator =>
          [">", "<", ">=", "<="].includes(operator.value)
        ),
      },
      {
        field: "status",
        headerName: t("Status", {
          ns: "Quotes",
        }),
        editable: false,
        sortable: false,
        flex: 1,
        minWidth: 80,
        valueGetter: (value, quote) =>
          `${quote.isPublished}-${quote.isBinding}-${quote.needsAgreement}-${quote.agreement}`,
        renderCell: ({ row: quote }) => (
          <QuoteStatusBadge small quote={quote} />
        ),
      },
      {
        field: "createdAt",
        headerName: t("Created", {
          ns: "Quotes",
        }),
        editable: false,
        sortable: true,
        flex: 1,
        valueGetter: (value, quote) => quote.createdAt ?? null,
        renderCell: ({ row: quote }) =>
          quote.createdAt ? getFormattedDate(quote.createdAt) : "–",
      },
      {
        field: "publishedAt",
        headerName: t("Sent", {
          ns: "Quotes",
        }),
        editable: false,
        sortable: true,
        type: "date",
        flex: 1,
        minWidth: 80,
        valueGetter: (value, quote) =>
          quote.publishedAt ? new Date(quote.publishedAt) : null,
        renderCell: ({ row: quote }) =>
          quote.publishedAt ? getFormattedDate(quote.publishedAt) : "–",
      },
      {
        field: "acceptedAt",
        headerName: t("Accepted", {
          ns: "Quotes",
        }),
        editable: false,
        sortable: true,
        type: "date",
        flex: 1,
        minWidth: 80,
        valueGetter: (value, quote) =>
          quote.acceptedAt ? new Date(quote.acceptedAt) : null,
        renderCell: ({ row: quote }) =>
          quote.acceptedAt ? getFormattedDate(quote.acceptedAt) : "–",
      },
      ...customFieldConfigs
        .filter(e => e.showAsColumnInList)
        .map(e => {
          return {
            field: `custom:${e.key}`,
            headerName: e.label,
            minWidth: 100,
            sortable: false,
            flex: 1,
            renderCell: ({ row: quote }) => {
              const field = quote.customFields.find(f => f.key === e.key);
              return getFieldDisplayValueForList(
                field
                  ? {
                      dataType: field.dataType,
                      key: field.key,
                      value: JSON.parse(field.value),
                    }
                  : undefined,
                e
              );
            },
          } as GridColDef<OutgoingQuoteFragment>;
        }),
    ],
    [customFieldConfigs, getFieldDisplayValueForList, getFormattedDate, t]
  );

  const getToPath = (item: { projectId: string; id: string }) =>
    `/projects/${item.projectId}/quotes/${item.id}`;

  const stateStore = useDataGridStateStore("OutgoingQuotes");
  const activeFilterChips = useQuotesActiveFilterChips({
    filters,
    setFilters,
  });

  return (
    <>
      <ListHeader
        SwitchViewButton={
          <SwitchCollectionViewButton
            allowedViews={ALLOWED_VIEWS}
            activeView={activeView}
            setActiveView={setActiveView}
          />
        }
        QuickFilter={
          <Stack direction="row" spacing={1} alignItems="center">
            {SectionTabs && (
              <>
                <div>{SectionTabs}</div>
                <Divider orientation="vertical" flexItem />
              </>
            )}
            <div>
              {isMinTablet ? (
                <>
                  <QuotesPublishingFilterButton
                    value={filters.filterIsPublished ?? []}
                    setValue={newValue =>
                      setFilters(state =>
                        newValue.length > 0
                          ? {
                              ...state,
                              filterIsPublished: newValue,
                            }
                          : omit(state, "filterIsPublished")
                      )
                    }
                  />
                  <QuotesBindingFilterButton
                    value={filters.filterIsBinding ?? []}
                    setValue={newValue =>
                      setFilters(state =>
                        newValue.length > 0
                          ? {
                              ...state,
                              filterIsBinding: newValue,
                            }
                          : omit(state, "filterIsBinding")
                      )
                    }
                  />
                  <QuotesAcceptanceFilterButton
                    value={filters.filterAgreement ?? []}
                    setValue={newValue =>
                      setFilters(state =>
                        newValue.length > 0
                          ? {
                              ...state,
                              filterAgreement: newValue,
                            }
                          : omit(state, "filterAgreement")
                      )
                    }
                  />
                  <TeamsFilterButton
                    value={filters.filterTeamIds ?? []}
                    setValue={newValue =>
                      setFilters(state =>
                        newValue.length > 0
                          ? { ...state, filterTeamIds: newValue }
                          : omit(state, "filterTeamIds")
                      )
                    }
                  />
                </>
              ) : undefined}
              {customQuickFilterSelect}
            </div>
          </Stack>
        }
        FilterButton={
          !isMinTablet ? (
            <ModalOpenButton
              Modal={QuotesFilterModal}
              modalProps={{
                filters,
                setFilters,
                resetFilters,
                tab: "outgoing",
              }}
            >
              <FilterButton />
            </ModalOpenButton>
          ) : undefined
        }
        SearchField={
          <DebouncedSearchInput
            placeholder={t("Search", {
              ns: "Global",
            })}
            defaultValue={filters.searchTerm}
            onChangeSearchTerm={(newValue: string) => {
              setFilters(filters => ({
                ...filters,
                searchTerm: newValue,
              }));
            }}
          />
        }
        ActiveFilterChips={
          <ListHeaderActiveFilters
            activeFilterChips={activeFilterChips}
            clearAllButton={
              <ListHeaderActiveFiltersActionButton
                label={t("Clear all", { ns: "Global" })}
                onClick={resetFilters}
              />
            }
            collapsedContent={
              <Typography variant="body2">
                {t("Active filters", { ns: "Global" })}
                <Typography
                  variant="body2"
                  component="span"
                  marginLeft={1}
                  fontWeight="bold"
                >
                  {t("{count} results", {
                    ns: "Global",
                    count: activeFilterChips.length,
                  })}
                </Typography>
              </Typography>
            }
            saveFiltersButton={addCustomFilterButton}
          />
        }
        marginBottom={2}
      />
      {(() => {
        switch (activeView) {
          case "table":
            return (
              <DataGrid
                stateStore={stateStore}
                loading={query.loading}
                hideFooter={totalCount === 0}
                columns={columns}
                rows={quotes}
                onRowClick={({ row }) => {
                  navigate(getToPath(row as OutgoingQuoteFragment));
                }}
                sortModel={sorting.map(s => ({
                  field: s.column,
                  sort: s.direction,
                }))}
                onSortModelChange={newModel => {
                  setSorting(
                    newModel.map(({ field, sort }) => ({
                      column: field as OutgoingQuotesSortBy,
                      direction: (sort ?? "asc") as SortDirection,
                    }))
                  );
                }}
                paginationModel={paginationModel}
                onPaginationModelChange={newPaginationModel => {
                  setPaginationModel(newPaginationModel);
                }}
                pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
                rowCount={totalCount}
              />
            );

          case "list":
            if (query.loading) return <LoadingSpinner />;
            if (totalCount === 0)
              return (
                <ErrorMessage
                  message={t("There are no items to display", {
                    ns: "Global",
                  })}
                />
              );
            return (
              <Stack width="100%" direction="column" spacing={1}>
                {quotes.map(quote => (
                  <CardItem
                    key={quote.id}
                    //@ts-ignore
                    component={Link}
                    to={getToPath(quote)}
                  >
                    <QuoteListItem quote={quote} />
                  </CardItem>
                ))}
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={paginationModel.page}
                  onPageChange={(event, newPage) => {
                    setPaginationModel({
                      pageSize: paginationModel.pageSize,
                      page: newPage,
                    });
                  }}
                  rowsPerPage={paginationModel.pageSize}
                  onRowsPerPageChange={event => {
                    setPaginationModel({
                      pageSize: parseInt(event.target.value, 10),
                      page: 0,
                    });
                  }}
                  labelRowsPerPage={t("Per page:", { ns: "Global" })}
                />
              </Stack>
            );
        }
      })()}
    </>
  );
}
